<template>
  <div class="state-city-fields">
    <!-- State -->
    <div :class="groupClass">
      <label :class="labelClass"
        >{{ $t("message.address_form.state") }} *</label
      >
      <SearchableSelect
        v-if="showStateSelect"
        :value="state"
        :options="stateSelectOptions"
        :pinned-options="manualOption"
        :input-class="inputClass"
        :disabled="loadingEstados"
        :placeholder="
          loadingEstados ? '...' : $t('message.address_form.select_state')
        "
        required
        @input="onStateSelected"
      />
      <template v-else>
        <input
          :value="state"
          type="search"
          :name="`ia-${_uid}-s`"
          autocomplete="off"
          data-lpignore="true"
          data-form-type="other"
          data-1p-ignore
          data-bwignore
          :class="inputClass"
          :placeholder="$t('message.address_form.enter_state')"
          required
          @input="$emit('update:state', $event.target.value)"
        />
        <a
          v-if="estados.length"
          href="#"
          class="text-xs underline mt-1"
          @click.prevent="backToStateList"
          >{{ $t("message.address_form.pick_from_list") }}</a
        >
      </template>
      <p v-if="stateNotInList" class="field-warning">
        <i class="mdi mdi-alert-outline"></i>
        {{ $t("message.address_form.state_not_in_list") }}
      </p>
    </div>

    <!-- City -->
    <div :class="groupClass">
      <label :class="labelClass">{{ $t("message.address_form.city") }} *</label>
      <SearchableSelect
        v-if="showCitySelect"
        :value="city"
        :options="citySelectOptions"
        :pinned-options="manualOption"
        :input-class="inputClass"
        :disabled="loadingCiudades"
        :placeholder="
          loadingCiudades ? '...' : $t('message.address_form.select_city')
        "
        required
        @input="onCitySelected"
      />
      <template v-else>
        <input
          :value="city"
          type="search"
          :name="`ia-${_uid}-c`"
          autocomplete="off"
          data-lpignore="true"
          data-form-type="other"
          data-1p-ignore
          data-bwignore
          :class="inputClass"
          :placeholder="$t('message.address_form.enter_city')"
          required
          @input="$emit('update:city', $event.target.value)"
        />
        <a
          v-if="ciudades.length"
          href="#"
          class="text-xs underline mt-1"
          @click.prevent="backToCityList"
          >{{ $t("message.address_form.pick_from_list") }}</a
        >
      </template>
      <p v-if="cityNotInList" class="field-warning">
        <i class="mdi mdi-alert-outline"></i>
        {{ $t("message.address_form.city_not_in_list") }}
      </p>
    </div>
  </div>
</template>

<script>
/**
 * Selectores de estado y ciudad con la caché de Infoauto (infoauto_estados /
 * infoauto_ciudades). El valor de estado es el cod_estado de Infoauto (VE/USA
 * lo mandan como código); la ciudad va por nombre. Elegir de la lista no es
 * obligatorio: "Otro" (o una caché vacía) deja escribirlos a mano. La lista es
 * siempre el modo por defecto; un valor que no está en la caché se muestra como
 * una opción más en vez de pasar a texto libre.
 */
import { getAddressEstados, getAddressCiudades } from "@/api/store/Infoauto.js";
import SearchableSelect from "@/components/global/SearchableSelect.vue";
const usStates = require("@/constants/states.json");

const MANUAL = "__manual__";

export default {
  name: "InfoautoStateCityFields",
  components: {
    SearchableSelect
  },
  props: {
    country: { type: String, default: "" },
    state: { type: String, default: "" },
    city: { type: String, default: "" },
    groupClass: { type: String, default: "form-field" },
    labelClass: { type: String, default: "form-label" },
    inputClass: { type: String, default: "regular full bordered" }
  },
  data() {
    return {
      MANUAL,
      estados: [],
      ciudades: [],
      ciudadesKey: null,
      stateManual: false,
      cityManual: false,
      loadingEstados: false,
      loadingCiudades: false
    };
  },
  watch: {
    country: {
      immediate: true,
      handler() {
        this.loadEstados();
      }
    },
    state() {
      this.normalizeState();
      this.loadCiudades();
    },
    city() {
      this.normalizeCity();
    }
  },
  computed: {
    showStateSelect() {
      return !this.stateManual && (this.loadingEstados || this.estados.length);
    },
    showCitySelect() {
      return (
        !this.cityManual && (this.loadingCiudades || this.ciudades.length > 0)
      );
    },
    // Si el valor guardado no está en la caché, se agrega como opción
    stateOptions() {
      if (!this.state || this.findEstado(this.state)) return this.estados;
      return [
        ...this.estados,
        { codEstado: this.state, nombreEstado: this.state }
      ];
    },
    cityOptions() {
      if (!this.city || this.findCiudad(this.city)) return this.ciudades;
      return [...this.ciudades, { nombreCiudad: this.city }];
    },
    stateSelectOptions() {
      return this.stateOptions.map(e => ({
        value: e.codEstado,
        label: e.nombreEstado
      }));
    },
    citySelectOptions() {
      return this.cityOptions.map(c => ({
        value: c.nombreCiudad,
        label: c.nombreCiudad
      }));
    },
    // Aviso cuando el valor no coincide con la caché (solo si hay lista)
    stateNotInList() {
      return (
        !!this.state &&
        !this.loadingEstados &&
        this.estados.length > 0 &&
        !this.findEstado(this.state)
      );
    },
    cityNotInList() {
      return (
        !!this.city &&
        !this.loadingCiudades &&
        this.ciudades.length > 0 &&
        !this.findCiudad(this.city)
      );
    },
    manualOption() {
      return [
        { value: MANUAL, label: this.$t("message.address_form.type_manually") }
      ];
    }
  },
  methods: {
    async loadEstados() {
      this.stateManual = false;
      this.cityManual = false;
      this.ciudadesKey = null;
      this.estados = [];
      this.ciudades = [];
      const country = (this.country || "").toUpperCase();
      if (country) {
        this.loadingEstados = true;
        try {
          const res = await getAddressEstados(country);
          this.estados = Array.isArray(res) ? res : [];
        } catch (e) {
          this.estados = [];
        } finally {
          this.loadingEstados = false;
        }
      }
      // Sin caché para USA seguimos ofreciendo la lista fija de estados
      if (!this.estados.length && country === "US") {
        this.estados = Object.keys(usStates).map(abbr => ({
          codEstado: abbr,
          nombreEstado: usStates[abbr]
        }));
      }
      this.normalizeState();
      await this.loadCiudades();
    },
    async loadCiudades() {
      const country = (this.country || "").toUpperCase();
      const key = `${country}|${this.state}`;
      if (key === this.ciudadesKey) {
        return;
      }
      this.ciudadesKey = key;
      this.ciudades = [];
      if (country && this.state && this.findEstado(this.state)) {
        this.loadingCiudades = true;
        try {
          const res = await getAddressCiudades(country, this.state);
          // Descarta respuestas de un estado que ya no está seleccionado
          if (key !== this.ciudadesKey) return;
          this.ciudades = Array.isArray(res) ? res : [];
        } catch (e) {
          this.ciudades = [];
        } finally {
          this.loadingCiudades = false;
        }
      }
      this.normalizeCity();
    },
    findEstado(value) {
      return this.estados.find(e => e.codEstado === value);
    },
    findCiudad(value) {
      const needle = (value || "").toLowerCase();
      return this.ciudades.find(
        c => (c.nombreCiudad || "").toLowerCase() === needle
      );
    },
    // Direcciones viejas o Google traen el nombre ("FLORIDA") o el código corto
    // ("FL") del estado, y la caché puede tenerlo con prefijo ("US-FL"): se
    // lleva al código de la lista.
    normalizeState() {
      if (!this.state || this.findEstado(this.state)) return;
      const needle = this.state.toLowerCase();
      const code = e => (e.codEstado || "").toLowerCase();
      const match =
        this.estados.find(
          e => (e.nombreEstado || "").toLowerCase() === needle
        ) ||
        this.estados.find(e => code(e).endsWith(`-${needle}`)) ||
        this.estados.find(e => code(e) && needle.endsWith(`-${code(e)}`));
      if (match) {
        this.$emit("update:state", match.codEstado);
      }
    },
    normalizeCity() {
      const match = this.findCiudad(this.city);
      if (match && match.nombreCiudad !== this.city) {
        this.$emit("update:city", match.nombreCiudad);
      }
    },
    onStateSelected(value) {
      if (value === MANUAL) {
        this.stateManual = true;
        this.$emit("update:state", "");
      } else {
        this.$emit("update:state", value);
      }
      this.$emit("update:city", "");
    },
    onCitySelected(value) {
      if (value === MANUAL) {
        this.cityManual = true;
        this.$emit("update:city", "");
        return;
      }
      this.$emit("update:city", value);
    },
    backToStateList() {
      this.stateManual = false;
      this.$emit("update:state", "");
      this.$emit("update:city", "");
    },
    backToCityList() {
      this.cityManual = false;
      this.$emit("update:city", "");
    }
  }
};
</script>

<style scoped>
.field-warning {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #b45309;
}

/* type="search" (para esquivar a LastPass) trae una "x" nativa en WebKit */
input[type="search"]::-webkit-search-cancel-button,
input[type="search"]::-webkit-search-decoration {
  -webkit-appearance: none;
  appearance: none;
}

/* Los dos campos son celdas del grid del formulario padre */
.state-city-fields {
  display: contents;
}
</style>
